<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">General</div>
        <h3 class="duallo-table-title">Model Cache</h3>
      </div>
      <button v-if="modelCacheRecords.length" class="btn-danger" @click="openDeleteAllDialog">
        <v-icon size="16" start>mdi-delete-sweep-outline</v-icon>
        Delete All
      </button>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton row-skeleton" />
      </div>

      <div v-else-if="modelCacheRecords.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-cube-outline</v-icon>
        <p class="empty-title">No cached models</p>
        <p class="empty-subtitle">Models you open get cached here for faster loading on this device.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-thumb"></th>
                <th class="col-name">Scan Name</th>
                <th class="col-name">Asset Name</th>
                <th class="col-desc">Description</th>
                <th class="col-size">Size</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="model in modelCacheRecords" :key="model.url">
                <td class="cell-thumb">
                  <div class="asset-thumb">
                    <img v-if="model.imagePath" :src="model.imagePath" alt="Asset" @error="useDefaultImage" />
                    <v-icon v-else size="24" color="#4a5a60">mdi-image-outline</v-icon>
                  </div>
                </td>
                <td class="cell-name">{{ model.scanName || '—' }}</td>
                <td class="cell-name">{{ model.name }}</td>
                <td class="cell-desc">{{ model.description || '—' }}</td>
                <td class="cell-size">{{ formatSize(model.sizeBytes) }}</td>
                <td class="cell-actions">
                  <button class="btn-danger-text" @click="openDeleteCacheDialog(model)">
                    <v-icon size="16">mdi-delete-outline</v-icon>
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="model in modelCacheRecords" :key="model.url" class="duallo-list-card card">
            <div class="card-head">
              <div class="asset-thumb sm">
                <img v-if="model.imagePath" :src="model.imagePath" alt="Asset" @error="useDefaultImage" />
                <v-icon v-else size="24" color="#4a5a60">mdi-image-outline</v-icon>
              </div>
              <div class="card-meta">
                <div class="card-name">{{ model.scanName || '—' }}</div>
                <div class="card-sub">{{ model.name }}</div>
              </div>
            </div>
            <p v-if="model.description" class="card-desc">{{ model.description }}</p>
            <div class="card-size">{{ formatSize(model.sizeBytes) }}</div>
            <div class="card-actions">
              <button class="btn-danger-text" @click="openDeleteCacheDialog(model)">
                <v-icon size="16">mdi-delete-outline</v-icon>
                Delete
              </button>
            </div>
          </article>
        </div>
      </template>
    </div>

    <v-dialog v-model="isDeleteCacheDialogVisible" max-width="500">
      <v-card class="confirm-dialog">
        <v-card-title class="dialog-title">Remove Cached Model?</v-card-title>
        <v-card-text class="dialog-body">
          <p>This only removes the model from this browser on this device.</p>
          <p>It does not affect server data. The model will download again when you open the asset.</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <button class="duallo-btn duallo-btn--ghost" @click="closeDeleteCacheDialog">Cancel</button>
          <button class="btn-danger" @click="confirmDeleteCache">
            <v-icon size="16" start>mdi-delete-outline</v-icon>
            Yes, Remove
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteAllDialogVisible" max-width="500">
      <v-card class="confirm-dialog">
        <v-card-title class="dialog-title">Remove All Cached Models?</v-card-title>
        <v-card-text class="dialog-body">
          <p>This removes every cached model from this browser on this device.</p>
          <p>It does not affect server data. Each model will download again when you next open its asset.</p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <button class="duallo-btn duallo-btn--ghost" @click="closeDeleteAllDialog">Cancel</button>
          <button class="btn-danger" @click="confirmDeleteAll">
            <v-icon size="16" start>mdi-delete-sweep-outline</v-icon>
            Yes, Remove All
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup>
import { defineOptions, onMounted, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import AssetService from '@/services/AssetService';
import ModelVersionService from '@/services/ModelVersionService';
import { useOrganizationStore } from '@/store/organizationStore';
import organizationSasService from '@/services/OrganizationSasService';
import { useDefaultImage } from '@/utils/ImageUtils';
import modelCache from '@/utils/ModelCache';

defineOptions({ name: "LocalModelCacheTable" })

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const assets = ref(null)
const organizationStore = useOrganizationStore();
const modelCacheRecords = ref([])
const isLoading = ref(true);
const isDeleteCacheDialogVisible = ref(false)
const isDeleteAllDialogVisible = ref(false)
const selectedCacheModel = ref(null)

onMounted(async () => {
  assets.value = await AssetService.getAssetsByOrganizationIdAsync(organizationStore.organizationId);
  await getCacheRecordsAsync();
})

// Cache key shape (query already stripped):
//   raw:   {root}/{assetId}/scans/{scanId}/model/{scanId}.ply
//   baked: {root}/{assetId}/modelversions/{versionId}/{scanId}.ply
// assetId is always the segment after the container root (index 4). The scanId is the file name in
// BOTH layouts, so derive it from the last segment rather than a fixed index — the baked layout shifts
// the scanId off index 6, which is why scan names stopped resolving once baked models were cached.
const ASSET_ID_INDEX = 4;

const scanIdFromUrl = (url) => url.split('/').pop().replace(/\.ply$/i, '');

const formatSize = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
};

// Memoise per-asset scan lookups so multiple cached scans of the same asset
// only trigger one getScansByAssetId call.
const scanNamesByAsset = new Map();

const getScanNameAsync = async (assetId, scanId) => {
  if (!scanNamesByAsset.has(assetId)) {
    const scans = await ModelVersionService.getScansByAssetId(assetId);
    scanNamesByAsset.set(assetId, new Map(scans.map(s => [s.id, s.name])));
  }
  return scanNamesByAsset.get(assetId).get(scanId) ?? null;
}

const getCacheRecordsAsync = async () => {
  try {
    const cacheName = modelCache.getCacheName();
    const requests = await modelCache.getAllRequestsAsync();

    for (const request of requests) {
      const splitValues = request.url.split('/');
      const asset = assets.value.find(a => a.id === splitValues[ASSET_ID_INDEX])
      if (!asset) continue;
      const imageSrc = await organizationSasService.getSignedUrl(asset.imagePath);
      const scanName = await getScanNameAsync(asset.id, scanIdFromUrl(request.url));
      const sizeBytes = await modelCache.getContentLengthAsync(request);

      modelCacheRecords.value.push({
        cacheName,
        url: request.url,
        scanName,
        name: asset.name,
        description: asset.description,
        imagePath: imageSrc,
        sizeBytes
      })
    }
  } finally {
    isLoading.value = false
  }
}

const openDeleteCacheDialog = (model) => {
  selectedCacheModel.value = model;
  isDeleteCacheDialogVisible.value = true;
}

const closeDeleteCacheDialog = () => {
  selectedCacheModel.value = null;
  isDeleteCacheDialogVisible.value = false;
}

const confirmDeleteCache = async () => {
  const isDeleted = await modelCache.deleteModelRecordAsync(selectedCacheModel.value);
  if (isDeleted) {
    const index = modelCacheRecords.value.findIndex(x => x.url === selectedCacheModel.value.url);
    if (index !== -1) modelCacheRecords.value.splice(index, 1);
  }
  closeDeleteCacheDialog();
}

const openDeleteAllDialog = () => { isDeleteAllDialogVisible.value = true; }
const closeDeleteAllDialog = () => { isDeleteAllDialogVisible.value = false; }

const confirmDeleteAll = async () => {
  await modelCache.clearAllAsync();
  modelCacheRecords.value = [];
  closeDeleteAllDialog();
}
</script>

<style scoped>
/* Skeleton rows are taller here than the shared default (56px vs 48px) */
.row-skeleton { height: 56px; }

/* ----- Empty state ----- */
.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

/* ----- Table-specific columns / cells ----- */
.col-thumb { width: 90px; }
.col-name { min-width: 140px; color: #fff; font-weight: 600; }
.col-size { width: 110px; white-space: nowrap; }
.col-actions { width: 130px; text-align: right; }

.cell-size { color: #b0bec5; white-space: nowrap; font-variant-numeric: tabular-nums; }
.card-size { font-size: 0.82rem; color: #8aa0aa; font-variant-numeric: tabular-nums; }

.cell-name { color: #fff; font-weight: 600; }
.cell-desc { color: #b0bec5; }
.cell-actions { text-align: right; }

.asset-thumb {
  width: 64px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
}

.asset-thumb.sm {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
}

.asset-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ----- Mobile cards ----- */
.card {
  flex: 0 0 auto;
  gap: 10px;
}

.card-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-meta { min-width: 0; }

.card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  word-break: break-word;
}

.card-sub {
  font-size: 0.82rem;
  color: #8aa0aa;
  word-break: break-word;
}

.card-desc {
  font-size: 0.88rem;
  color: #b0bec5;
  margin: 0;
  line-height: 1.45;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-danger-text {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #f87171;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-danger-text:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* ----- Confirm dialog (Vuetify v-card shell) ----- */
.confirm-dialog {
  border-radius: 14px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
  padding: 8px;
}

.dialog-title {
  font-weight: 700;
  color: #fff;
  padding: 12px 12px 6px;
  font-size: 1.1rem;
}

.dialog-body {
  color: #b0bec5;
  padding: 0 12px 12px;
}

.dialog-body p { margin: 0 0 8px; line-height: 1.5; }

.dialog-actions {
  padding: 8px 12px 12px;
  gap: 8px;
}

/* Dialog ghost button keeps its larger padding / radius (differs from shared) */
.duallo-btn--ghost {
  padding: 8px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
  color: #cfd8dc;
  font-size: inherit;
  font-weight: 600;
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.18);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-weight: 700;
  font: inherit;
  cursor: pointer;
}
</style>
